import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../../utils/axios';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Spinner,
    Heading,
    Text,
    useToast,
    Input,
    HStack,
    Switch,
    FormControl,
    FormLabel,
    InputGroup,
    InputLeftElement
} from "@chakra-ui/react";
import { SearchIcon } from '@chakra-ui/icons';

const GroupMembersList = ({ usergroupId }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchString, setSearchString] = useState('');
    const [activeOnly, setActiveOnly] = useState(true);
    const toast = useToast();
    const isFetching = useRef(false);

    const fetchMembers = async () => {
        if (isFetching.current) return;
        isFetching.current = true;

        try {
            const response = await axiosInstance.post(
                `/dummy/get-user-group-members`,
                {
                    h_usergroup_id: usergroupId,
                    searchString: searchString,
                    currentPage: currentPage,
                    itemsPerPage: 8,
                    activeYN: activeOnly
                }
            );

            if (response.data && response.data.members) {
                setMembers(response.data.members);
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to fetch group members",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    };

    const handleToggleActive = async (memberId, currentStatus) => {
        try {
            await axiosInstance.post(
                `/dummy/toggle-member-active`,
                {
                    alloc_h_user_h_usergroup_id: memberId,
                    active_yn: !currentStatus
                }
            );

            setMembers(members.map(member => 
                member.alloc_h_user_h_usergroup_id === memberId 
                    ? { ...member, active_yn: !currentStatus }
                    : member
            ));

            toast({
                title: "Success",
                description: "Member status updated successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update member status",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        fetchMembers();
    }, [usergroupId, currentPage, searchString, activeOnly]);

    const handleSearchChange = (e) => {
        setSearchString(e.target.value);
    };

    const handleActiveToggle = (e) => {
        setActiveOnly(e.target.checked);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <Spinner size="xl" />
            </Box>
        );
    }

    return (
        <Box mt={8}>
            <Heading size="md" mb={4}>Group Members</Heading>
            
            {/* Search and Filter Controls */}
            <HStack spacing={4} mb={4}>
                <InputGroup>
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.300" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search members..."
                        value={searchString}
                        onChange={handleSearchChange}
                        width="300px"
                    />
                </InputGroup>
                
                <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="active-toggle" mb="0">
                        Inactive or Active Members
                    </FormLabel>
                    <Switch
                        id="active-toggle"
                        isChecked={activeOnly}
                        onChange={handleActiveToggle}
                    />
                </FormControl>
            </HStack>

            <Box overflowX="auto">
                <Table variant="striped" size="md">
                    <Thead>
                        <Tr>
                            <Th>ID</Th>
                            <Th>First Name</Th>
                            <Th>Last Name</Th>
                            <Th>Email</Th>
                            <Th>Token</Th>
                            <Th>Referral Code</Th>
                            <Th>Status</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {Array.isArray(members) && members.map((member) => (
                            <Tr key={member.alloc_h_user_h_usergroup_id}>
                                <Td>{member.alloc_h_user_h_usergroup_id || 'N/A'}</Td>
                                <Td>{member.wp_first_name || 'N/A'}</Td>
                                <Td>{member.wp_last_name || 'N/A'}</Td>
                                <Td>{member.wp_user_email || 'N/A'}</Td>
                                <Td>{member.tokenyn || 'N/A'}</Td>
                                <Td>{member.wp_dgn_referral_code || 'N/A'}</Td>
                                <Td>
                                    <Switch
                                        isChecked={member.active_yn}
                                        onChange={() => handleToggleActive(
                                            member.alloc_h_user_h_usergroup_id,
                                            member.active_yn
                                        )}
                                        colorScheme={member.active_yn ? "green" : "gray"}
                                    />
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            {(!Array.isArray(members) || members.length === 0) && (
                <Text mt={4}>No members found in this group.</Text>
            )}
        </Box>
    );
};

export default GroupMembersList; 